import { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { ArrowLeft, RefreshCw, DollarSign, Send, Package, BarChart3, AlertTriangle, Slack } from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";

import { QuotesTab } from "../components/admin/QuotesTab";
import { OrdersTab } from "../components/admin/OrdersTab";
import { FreightTab } from "../components/admin/FreightTab";
import { SupportTab } from "../components/admin/SupportTab";
import { TelemetryTab } from "../components/admin/TelemetryTab";
import { PortalTab } from "../components/admin/PortalTab";

const BACKEND_URL = process.env.BACKEND_URL;

const API = `${BACKEND_URL}/api`;

export default function AdminPage() {
  const [quotes, setQuotes] = useState([]);
  const [orders, setOrders] = useState([]);
  const [freightQuotes, setFreightQuotes] = useState([]);
  const [supportTickets, setSupportTickets] = useState([]);
  const [telemetryData, setTelemetryData] = useState(null);
  const [portalResources, setPortalResources] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [healthStatus, setHealthStatus] = useState(null);
  const [activeTab, setActiveTab] = useState("quotes");

  useEffect(() => {
    fetchAllData();
    checkHealth();
  }, []);

  const fetchAllData = async () => {
    setIsLoading(true);
    try {
      const results = await Promise.all([
        axios.get(`${API}/quotes`),
        axios.get(`${API}/orders`),
        axios.get(`${API}/freight/quotes`),
        axios.get(`${API}/portal/support`),
        axios.get(`${API}/telemetry/dashboard`),
        axios.get(`${API}/portal/resources`)
      ]);
      setQuotes(results[0].data);
      setOrders(results[1].data);
      setFreightQuotes(results[2].data);
      setSupportTickets(results[3].data);
      setTelemetryData(results[4].data);
      setPortalResources(results[5].data);
    } catch (error) {
      console.error("Fetch error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const checkHealth = async () => {
    try {
      const res = await axios.get(`${API}/health`);
      setHealthStatus(res.data);
    } catch (error) {
      setHealthStatus({ status: "error" });
    }
  };

  const sendSlackNotification = async (message) => {
    try {
      await axios.post(`${API}/slack/notify`, { message });
      toast.success("Slack notification sent");
    } catch (error) {
      toast.error("Failed to send notification");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  const getStatusBadge = (status) => {
    const colors = {
      new: "bg-blue-100 text-blue-800", contacted: "bg-yellow-100 text-yellow-800", quoted: "bg-purple-100 text-purple-800",
      paid: "bg-green-100 text-green-800", in_production: "bg-orange-100 text-orange-800", shipped: "bg-cyan-100 text-cyan-800",
      delivered: "bg-emerald-100 text-emerald-800", pending: "bg-gray-100 text-gray-800", qc_complete: "bg-indigo-100 text-indigo-800",
      requested: "bg-blue-100 text-blue-800", booked: "bg-purple-100 text-purple-800", in_transit: "bg-cyan-100 text-cyan-800",
      open: "bg-red-100 text-red-800", in_progress: "bg-yellow-100 text-yellow-800", resolved: "bg-green-100 text-green-800", closed: "bg-gray-100 text-gray-800"
    };
    return colors[status] || "bg-gray-100 text-gray-800";
  };

  return (
    <div className="min-h-screen bg-zinc-50">
      <header className="bg-zinc-950 text-white py-4 px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2 text-zinc-400 hover:text-white">
              <ArrowLeft className="h-4 w-4" /> Back
            </Link>
            <h1 className="font-heading text-2xl font-bold uppercase">Admin Dashboard</h1>
          </div>
          <div className="flex items-center gap-4">
            {healthStatus && (
              <div className="flex items-center gap-2 text-sm">
                <span className={`h-2 w-2 rounded-full ${healthStatus.airtable_configured ? 'bg-green-500' : 'bg-yellow-500'}`}></span>
                <span className="text-zinc-400">Airtable</span>
                <span className={`h-2 w-2 rounded-full ${healthStatus.slack_configured ? 'bg-green-500' : 'bg-yellow-500'}`}></span>
                <span className="text-zinc-400">Slack</span>
              </div>
            )}
            <Button variant="outline" size="sm" onClick={fetchAllData} data-testid="refresh-btn" className="border-zinc-700 text-zinc-300 hover:bg-zinc-800">
              <RefreshCw className="h-4 w-4 mr-2" /> Refresh
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {telemetryData && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <StatCard icon={Package} label="Orders" value={orders.length} />
            <StatCard icon={DollarSign} label="Revenue (7d)" value={`$${(telemetryData.total_revenue_7d || 0).toFixed(2)}`} />
            <StatCard icon={BarChart3} label="Machines" value={telemetryData.active_machines || 0} />
            <StatCard icon={AlertTriangle} label="Low Stock" value={telemetryData.total_low_stock_alerts || 0} color="text-orange-600" />
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white border border-zinc-200 flex-wrap h-auto p-1">
            <TabsTrigger value="quotes" className="font-heading uppercase text-xs">Quotes ({quotes.length})</TabsTrigger>
            <TabsTrigger value="orders" className="font-heading uppercase text-xs">Orders ({orders.length})</TabsTrigger>
            <TabsTrigger value="freight" className="font-heading uppercase text-xs">Freight ({freightQuotes.length})</TabsTrigger>
            <TabsTrigger value="support" className="font-heading uppercase text-xs">Support ({supportTickets.length})</TabsTrigger>
            <TabsTrigger value="telemetry" className="font-heading uppercase text-xs">Telemetry</TabsTrigger>
            <TabsTrigger value="portal" className="font-heading uppercase text-xs">Portal</TabsTrigger>
          </TabsList>

          <TabsContent value="quotes">
            <QuotesTab quotes={quotes} isLoading={isLoading} getStatusBadge={getStatusBadge} formatDate={formatDate} fetchAllData={fetchAllData} />
          </TabsContent>
          <TabsContent value="orders">
            <OrdersTab orders={orders} isLoading={isLoading} getStatusBadge={getStatusBadge} formatDate={formatDate} fetchAllData={fetchAllData} />
          </TabsContent>
          <TabsContent value="freight">
            <FreightTab freightQuotes={freightQuotes} orders={orders} isLoading={isLoading} getStatusBadge={getStatusBadge} fetchAllData={fetchAllData} />
          </TabsContent>
          <TabsContent value="support">
            <SupportTab tickets={supportTickets} isLoading={isLoading} getStatusBadge={getStatusBadge} formatDate={formatDate} fetchAllData={fetchAllData} />
          </TabsContent>
          <TabsContent value="telemetry">
            <TelemetryTab telemetryData={telemetryData} isLoading={isLoading} />
          </TabsContent>
          <TabsContent value="portal">
            <PortalTab resources={portalResources} isLoading={isLoading} fetchAllData={fetchAllData} />
          </TabsContent>
        </Tabs>

        <div className="mt-8 bg-white rounded-sm border border-zinc-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <Slack className="h-5 w-5 text-zinc-600" />
            <h3 className="font-heading text-lg font-semibold uppercase">Slack Notification</h3>
          </div>
          <div className="flex gap-4">
            <Input id="slack-message" placeholder="Type a message..." className="flex-1" />
            <Button onClick={() => { const msg = document.getElementById("slack-message").value; if (msg) sendSlackNotification(msg); }} className="bg-blue-600 hover:bg-blue-700">
              <Send className="h-4 w-4 mr-2" /> Send
            </Button>
          </div>
          <p className="text-xs text-zinc-500 mt-2">{healthStatus?.slack_configured ? "✓ Slack configured" : "⚠ Slack not configured"}</p>
        </div>
      </main>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color = "text-zinc-900" }) {
  return (
    <div className="bg-white rounded-sm border border-zinc-200 p-4">
      <div className="flex items-center gap-3">
        <Icon className={`h-5 w-5 ${color === "text-orange-600" ? "text-orange-500" : "text-zinc-400"}`} />
        <div>
          <p className="text-xs text-zinc-500 uppercase">{label}</p>
          <p className={`font-heading text-2xl font-bold ${color}`}>{value}</p>
        </div>
      </div>
    </div>
  );
}
